"use client";

import { Search } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";

import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";

import { EmptyState } from "@/components/states/empty-state";
import { QueryBoundary } from "@/components/states/query-boundary";
import { Skeleton } from "@/components/states/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";

import { countryName } from "@/features/auth/data/countries";
import { useMarketSearch } from "@/features/devices/hooks/use-devices";
import type { MarketDevice, RentDuration } from "@/features/devices/types";
import {
  DURATION_ABBREVIATION,
  DURATION_ORDER,
  LANDING_CARD,
  LANDING_PAGE_SIZE,
  LANDING_SEARCH,
  LANDING_STATS_LIMIT,
  RENT_HREF,
} from "@/features/marketing/components/landing/landing-content";
import { flagEmoji } from "@/features/workers/components/worker-bits";

/**
 * The landing page's data + presentational primitives: the market query, the
 * search form's behavior, and the device facts a card must show. Kept separate
 * from `landing-view` so the parity-critical bits (what a card says, which
 * durations are offered, what the search does) live in one place rather than
 * inline in layout markup.
 *
 * Written for the four-way bake-off (6f7e289) and promoted with the winner —
 * the same path `contact-shared` / `about-shared` took.
 */

// --- Data --------------------------------------------------------------------

/**
 * Search + results for the landing grid. Page is fixed at 1 by design: legacy
 * renders its pager only for signed-in users, so a signed-out visitor sees the
 * first 9 devices and searches to narrow, never pages. Search submits on
 * enter/click (not per keystroke), matching the live form.
 */
export function useLandingMarket() {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");

  const query = useMarketSearch({
    search,
    page: 1,
    limit: LANDING_STATS_LIMIT,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(input.trim());
  };

  return { input, setInput, search, submit, query };
}

/** Offered durations only — a duration the owner didn't price is omitted, as
 * live does, rather than rendered as an empty row. */
export function offeredPrices(
  device: MarketDevice,
): { duration: RentDuration; amount: number }[] {
  return DURATION_ORDER.flatMap((duration) => {
    const amount = device.prices[duration];
    return amount == null ? [] : [{ duration, amount }];
  });
}

/** `$0.12/hr` — the exact string shape live puts in its price selector. */
export function priceLabel(duration: RentDuration, amount: number): string {
  return `${formatCurrency(amount)}/${DURATION_ABBREVIATION[duration]}`;
}

/** The four spec rows, in live's order, with live's value formatting. */
export function deviceSpecs(device: MarketDevice) {
  return [
    { label: LANDING_CARD.specs.name, value: device.name || "—" },
    {
      label: LANDING_CARD.specs.version,
      value: device.androidVersion ? `Android ${device.androidVersion}` : "—",
    },
    {
      label: LANDING_CARD.specs.ram,
      value: device.ramMb ? `${device.ramMb}MB` : "—",
    },
    { label: LANDING_CARD.specs.processor, value: device.processor || "—" },
  ];
}

/** `{brand} {model}` — the card's H-level title on live. */
export function deviceTitle(device: MarketDevice): string {
  return [device.brand, device.model].filter(Boolean).join(" ") || device.name;
}

// --- Atoms -------------------------------------------------------------------

/** Search field + button. `className` places it; the field, placeholder, label
 * and submit copy are fixed by the parity contract. */
export function LandingSearch({
  input,
  setInput,
  onSubmit,
  pending,
  className,
  inputClassName,
}: {
  input: string;
  setInput: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  pending: boolean;
  className?: string;
  inputClassName?: string;
}) {
  return (
    <form
      onSubmit={onSubmit}
      role="search"
      className={cn("flex w-full gap-2", className)}
    >
      <div className="relative flex-1">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          aria-label={LANDING_SEARCH.label}
          placeholder={LANDING_SEARCH.placeholder}
          className={cn("pl-9", inputClassName)}
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
      </div>
      <Button type="submit" disabled={pending}>
        {LANDING_SEARCH.submit}
      </Button>
    </form>
  );
}

/** Country flag glyph with an accessible name — live uses a flag image with a
 * tooltip; the emoji needs no asset and carries the name in its label. */
export function CountryFlagChip({ code }: { code: string }) {
  const name = countryName(code) || code?.toUpperCase() || "Unknown";
  const glyph = flagEmoji(code);
  return (
    <span
      className="shrink-0 text-lg leading-none"
      title={name}
      aria-label={name}
      role="img"
    >
      {glyph || "🏳️"}
    </span>
  );
}

/** `By {username}`, linking to the owner's public profile as live does. */
export function OwnerByline({
  device,
  className,
}: {
  device: MarketDevice;
  className?: string;
}) {
  const username = device.ownerUsername || "unknown";
  return (
    <p className={cn("truncate text-sm text-muted-foreground", className)}>
      {LANDING_CARD.byPrefix}{" "}
      {device.ownerUserId ? (
        <Link
          href={`/user_profile?id=${device.ownerUserId}`}
          className="font-medium text-foreground hover:underline"
        >
          {username}
        </Link>
      ) : (
        <span className="font-medium text-foreground">{username}</span>
      )}
    </p>
  );
}

/**
 * Duration picker + Rent Now, the card's action row. Live pairs a `<select>` of
 * offered prices with the button; the selection is local because a signed-out
 * visitor can't rent anyway — it's a price preview, and the button hands off to
 * the authenticated marketplace.
 */
export function RentRow({
  device,
  className,
  selectClassName,
  buttonClassName,
}: {
  device: MarketDevice;
  className?: string;
  selectClassName?: string;
  buttonClassName?: string;
}) {
  const prices = useMemo(() => offeredPrices(device), [device]);
  const first = prices[0];
  const [duration, setDuration] = useState<RentDuration | null>(
    first?.duration ?? null,
  );

  return (
    <div className={cn("flex items-center gap-3", className)}>
      {first ? (
        <NativeSelect
          aria-label={`Rental duration for ${deviceTitle(device)}`}
          className={cn(
            "h-10 w-auto flex-1 font-mono text-sm",
            selectClassName,
          )}
          value={duration ?? first.duration}
          onChange={(e) => setDuration(e.target.value as RentDuration)}
        >
          {prices.map(({ duration: d, amount }) => (
            <option key={d} value={d}>
              {priceLabel(d, amount)}
            </option>
          ))}
        </NativeSelect>
      ) : (
        <span className="flex-1 text-sm text-muted-foreground">
          Price on request
        </span>
      )}
      <Button asChild className={cn("shrink-0", buttonClassName)}>
        <Link href={RENT_HREF}>{LANDING_CARD.rent}</Link>
      </Button>
    </div>
  );
}

// --- Results boundary --------------------------------------------------------

/**
 * One loading/empty/error contract for both of the page's device-backed
 * regions, so the two can't drift apart. `skeletonClassName` lets each caller
 * match the shape of its own tile; everything else is fixed.
 *
 * `quiet` is for the SECOND boundary on the same query — the stat strip derives
 * its numbers from the same devices the grid renders below. Without it, one
 * failed request paints two error cards and the empty state twice. A quiet
 * boundary keeps its skeleton (so the layout doesn't jump while loading) but
 * renders nothing once there is nothing to summarise, leaving the grid's
 * boundary as the single place a failure or an empty result is reported.
 */
export function LandingResults({
  query,
  search,
  skeletonClassName = "h-64 w-full rounded-2xl",
  skeletonCount = LANDING_PAGE_SIZE,
  gridClassName = "grid gap-6 sm:grid-cols-2 lg:grid-cols-3",
  quiet = false,
  children,
}: {
  query: ReturnType<typeof useLandingMarket>["query"];
  search: string;
  skeletonClassName?: string;
  /** Placeholder count. Defaults to a full page of devices, which is right for
   * the grid but wrong for the stat strip — that one is a fixed four cells, and
   * nine placeholders would load in as three ragged rows and then snap to one. */
  skeletonCount?: number;
  gridClassName?: string;
  quiet?: boolean;
  children: (devices: MarketDevice[], total: number) => React.ReactNode;
}) {
  return (
    <QueryBoundary
      query={query}
      loadingFallback={
        <div className={gridClassName}>
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton key={i} className={skeletonClassName} />
          ))}
        </div>
      }
      isEmpty={(d) => d.devices.length === 0}
      emptyFallback={
        // An empty fragment, NOT null: QueryBoundary resolves its fallback with
        // `emptyFallback ?? <EmptyState title="Nothing here yet" />`, so a null
        // here falls through to that generic default and the quiet boundary
        // would render a second, wrong empty state above the real one.
        quiet ? (
          <></>
        ) : (
          <EmptyState
            title="No devices found"
            description={
              search
                ? `No rentable devices match "${search}". Try a different search.`
                : "There are no devices on the marketplace right now. Check back soon."
            }
          />
        )
      }
      errorTitle="We couldn't load the marketplace"
      {...(quiet ? { errorFallback: () => null } : {})}
    >
      {/* Hands over the WHOLE fetched fleet: the stat strip has to count all of
          it, and the grid slices to LANDING_PAGE_SIZE itself. */}
      {(data) => children(data.devices, data.total)}
    </QueryBoundary>
  );
}
